import { labelAgentV2Citations, kAgentV2UnverifiedCitationLabel } from '@/agent-v2/references/agent-v2-citation-labels'
import { canOpenAgentV2Reference } from '@/agent-v2/references/agent-v2-reference-opening'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { replaceCitations } from '@/util/agent-citations'

export const kArtifactLegalCitationIdAttr = 'data-agent-v2-legal-citation-id'
export const kArtifactLegalCitationResolvedAttr = 'data-agent-v2-legal-citation-resolved'
export const kArtifactLegalCitationCanOpenAttr = 'data-agent-v2-legal-citation-can-open'
export const kArtifactReferenceMarkerIdAttr = 'data-agent-v2-citation-id'
export const kArtifactReferenceMarkerLabelAttr = 'data-agent-v2-citation-label'
export const kArtifactReferenceMarkerResolvedAttr = 'data-agent-v2-citation-resolved'
export const kArtifactReferenceMarkerCanOpenAttr = 'data-agent-v2-citation-can-open'

/**
 * Highlight marking the citation whose source is open. A citation rendered as several elements
 * takes the fill on all of them and an edge only where the whole citation starts and ends, so it
 * draws as one shape; one rendered as a single element takes all three.
 *
 * The negative margins let the highlight sit wider than the text it covers without moving that
 * text as it appears and clears.
 */
export const kActiveCitationHighlightFill = 'bg-brand-200'
export const kActiveCitationHighlightLeadingEdge = 'rounded-l-lg pl-1 -ml-1'
export const kActiveCitationHighlightTrailingEdge = 'rounded-r-lg pr-1 -mr-1'
export const kActiveCitationHighlightClass = `${kActiveCitationHighlightFill} ${kActiveCitationHighlightLeadingEdge} ${kActiveCitationHighlightTrailingEdge}`

/**
 * Replace raw citation tokens with identity-only spans for ProseMirror parsing.
 *
 * The spans deliberately contain no viewer URL. Citation clicks resolve the stored
 * citation id against the live reference map before creating a viewer session.
 * `opensInPanel` says whether a click opens the draft workspace's chat panel or a new
 * browser tab (the default), which decides whether a panel-only source can open.
 */
export function replaceAgentV2ArtifactCitationsWithSpans(
  markdown: string,
  referencesByCitationId: Record<string, AgentV2Reference> | undefined,
  { opensInPanel = false }: { opensInPanel?: boolean } = {}
): string {
  const labeledCitations = labelAgentV2Citations(markdown, referencesByCitationId)
  let occurrenceIndex = 0

  return replaceCitations(markdown, (_token, citation) => {
    const labeledCitation = labeledCitations[occurrenceIndex]
    occurrenceIndex += 1
    if (!labeledCitation || labeledCitation.citationId !== citation.citationId) {
      return citation.authorityText === null
        ? referenceMarkerSpan(citation.citationId, kAgentV2UnverifiedCitationLabel, false, false)
        : legalCitationSpan(citation.citationId, citation.authorityText, false, false)
    }

    const reference = referencesByCitationId?.[citation.citationId]
    const isResolved = labeledCitation.isResolved
    const canOpen = Boolean(reference && canOpenAgentV2Reference(reference, { opensInPanel }))

    if (citation.authorityText !== null) {
      return legalCitationSpan(citation.citationId, citation.authorityText, isResolved, canOpen)
    }

    return referenceMarkerSpan(citation.citationId, labeledCitation.label ?? kAgentV2UnverifiedCitationLabel, isResolved, canOpen)
  })
}

function legalCitationSpan(citationId: string, authorityText: string, isResolved: boolean, canOpen: boolean): string {
  return `<span ${kArtifactLegalCitationIdAttr}="${encodeAttribute(
    citationId
  )}" ${kArtifactLegalCitationResolvedAttr}="${isResolved}" ${kArtifactLegalCitationCanOpenAttr}="${canOpen}">${authorityText}</span>`
}

function referenceMarkerSpan(citationId: string, label: string, isResolved: boolean, canOpen: boolean): string {
  return `<span ${kArtifactReferenceMarkerIdAttr}="${encodeAttribute(citationId)}" ${kArtifactReferenceMarkerLabelAttr}="${encodeAttribute(
    label
  )}" ${kArtifactReferenceMarkerResolvedAttr}="${isResolved}" ${kArtifactReferenceMarkerCanOpenAttr}="${canOpen}">[${encodeAttribute(citationId)}]</span>`
}

function encodeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
